'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { ArrowLeft, Calendar, ChevronRight, Tag, Clock, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import type { BlogListItem } from '@/data/blog-posts';

const categories = ['All Posts', 'Guide', 'Company', 'Industry Insights', 'Global Business', 'Best Practices', 'Technology', 'Freelancer Resources'];

// Rendered on the server with every post, so the list is in the HTML without JavaScript.
// Category filtering and the newsletter form take over after hydration.
export default function BlogIndexClient({ posts }: { posts: BlogListItem[] }) {
  const [filteredPosts, setFilteredPosts] = useState<BlogListItem[]>(posts);
  const [selectedCategory, setSelectedCategory] = useState('All Posts');

  // Newsletter signup state
  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  // Filter posts by category
  useEffect(() => {
    if (selectedCategory === 'All Posts') {
      setFilteredPosts(posts);
    } else {
      setFilteredPosts(posts.filter(post => post.category === selectedCategory));
    }
  }, [selectedCategory, posts]);

  // Newsletter signup handler
  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterStatus('loading');

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setNewsletterStatus('success');
        setEmail('');
      } else {
        setNewsletterStatus('error');
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      setNewsletterStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto max-w-5xl px-4 py-16">
        {/* Header with navigation */}
        <div className="mb-12 flex items-center justify-between">
          <Link 
            href="/" 
            className="inline-flex items-center text-white hover:text-gray-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <Link href="/" className="text-2xl font-black tracking-tight text-white hover:text-gray-300 transition-colors">
            Talksie
          </Link>
        </div>

        {/* Hero Section */}
        <header className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-6 text-white">Talksie Blog</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Expert insights on interpretation, language services, and global business communication. 
            Learn from industry professionals and discover best practices for international success.
          </p>
        </header>

        {/* Enhanced Category Filter */}
        <section className="mb-12">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm border transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white border-blue-600 shadow-lg'
                    : 'bg-gray-800 text-gray-300 border-gray-700 hover:border-gray-600 hover:bg-gray-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
          
          {/* Results count */}
          <div className="text-center mt-4">
            <p className="text-gray-400 text-sm">
              {filteredPosts.length} {filteredPosts.length === 1 ? 'post' : 'posts'} 
              {selectedCategory !== 'All Posts' && ` in ${selectedCategory}`}
            </p>
          </div>
        </section>

        {/* Blog Posts */}
        <main className="space-y-8">
          {filteredPosts.length > 0 ? (
            filteredPosts.map((post, index) => (
              <article 
                key={post.slug} 
                className="group bg-gray-900 border border-gray-800 rounded-lg p-8 hover:border-gray-700 transition-all duration-300 hover:shadow-xl"
              >
                {/* Post Header */}
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <span className="inline-flex items-center px-3 py-1 bg-blue-900/30 text-blue-300 text-sm rounded-full border border-blue-600">
                    <Tag className="w-3 h-3 mr-1" />
                    {post.category}
                  </span>
                  <div className="flex items-center text-gray-400 text-sm">
                    <Calendar className="w-4 h-4 mr-2" />
                    <time dateTime={post.dateISO}>
                      {post.date}
                    </time>
                  </div>
                  <div className="flex items-center text-gray-500 text-sm">
                    <Clock className="w-4 h-4 mr-1" />
                    <span>{post.readTime}</span>
                  </div>
                </div>

                {/* Post Content */}
                <h2 className="text-2xl font-semibold mb-4 group-hover:text-blue-400 transition-colors">
                  <Link 
                    href={`/blog/${post.slug}`} 
                    className="text-white hover:text-blue-400 transition-colors"
                  >
                    {post.title}
                  </Link>
                </h2>
                
                <p className="text-gray-300 leading-relaxed mb-6 line-clamp-3">
                  {post.excerpt}
                </p>

                {/* Read More Link */}
                <Link 
                  href={`/blog/${post.slug}`} 
                  className="inline-flex items-center text-blue-400 hover:text-blue-300 font-medium group/link"
                >
                  Read full article
                  <ChevronRight className="w-4 h-4 ml-1 transition-transform group-hover/link:translate-x-1" />
                </Link>
              </article>
            ))
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-500 mb-4">
                <Tag className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-300 mb-2">No posts found</h3>
              <p className="text-gray-400">Try selecting a different category or check back later for new content.</p>
            </div>
          )}
        </main>

        {/* Newsletter Signup Section - Client-side implementation */}
        <section className="mt-16 pt-8 border-t border-gray-800">
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold mb-2 flex items-center justify-center gap-2">
              <Mail className="h-6 w-6 text-blue-400" />
              Stay Updated
            </h3>
            <p className="text-gray-400">
              Get notified when we publish new blog posts and industry insights
            </p>
          </div>
          
          <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto">
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                disabled={newsletterStatus === 'loading'}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {newsletterStatus === 'loading' ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                    Subscribing...
                  </>
                ) : (
                  'Subscribe'
                )}
              </button>
            </div>
            
            {/* Status Messages */}
            {newsletterStatus === 'success' && (
              <div className="flex items-center justify-center gap-2 mt-3 text-green-400 text-sm">
                <CheckCircle className="h-4 w-4" />
                <span>Successfully subscribed! Thank you for joining our newsletter.</span>
              </div>
            )}
            {newsletterStatus === 'error' && (
              <div className="flex items-center justify-center gap-2 mt-3 text-red-400 text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>Subscription failed. Please try again or contact us for help.</span>
              </div>
            )}
          </form>
        </section>

        {/* Call to Action */}
        <section className="mt-16 text-center">
          <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-800 rounded-lg p-12">
            <h2 className="text-3xl font-bold mb-6 text-white">Ready to Connect with Professional Interpreters?</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Join thousands of businesses who trust Talksie for their international communication needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                href="/find-interpreter"
                className="inline-flex items-center px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                Find an Interpreter
              </Link>
              <Link 
                href="/signup"
                className="inline-flex items-center px-8 py-4 border border-gray-600 text-gray-300 font-semibold rounded-lg hover:bg-gray-800 hover:text-white transition-colors"
              >
                Join as Interpreter or Translator
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-20 pt-8 border-t border-gray-800 text-center">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400">&copy; 2025 Talksie Inc. All rights reserved.</p>
            <div className="flex space-x-6">
              <Link href="/contact" className="text-gray-400 hover:text-white transition-colors text-sm">
                Contact
              </Link>
              <Link href="/about" className="text-gray-400 hover:text-white transition-colors text-sm">
                About
              </Link>
              <Link href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors text-sm">
                Privacy
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
